import React from 'react';
import {
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Spinner,
  Text,
  tokens,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { usePdmAssignmentStyles } from '../../styles/PdmAssignmentPage.styles';
import { PDM_REGION_COLORS, type PdmRegion } from '../../constants/pdmRegions';
import { RoleAssignmentCell } from './RoleAssignmentCell';
import type {
  PdmAssignment,
  PdmDirectoryEntry,
  RegionalLeadGroup,
} from '../../models/pdmAssignment';

interface RegionalLeadsEditorPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  regions: readonly string[];
  groups: RegionalLeadGroup[];
  pdmPool: PdmDirectoryEntry[];
  onSearch: (term: string) => Promise<PdmDirectoryEntry[]>;
  onAdd: (region: string, entry: PdmDirectoryEntry) => void;
  onRemove: (region: string, lead: PdmAssignment) => void;
  canEdit: boolean;
  mutatingRegion: string | null;
  loadError?: string | null;
  loading?: boolean;
  onRetryLoad?: () => void;
}
export const RegionalLeadsEditorPanel: React.FC<RegionalLeadsEditorPanelProps> = ({
  open,
  onOpenChange,
  regions,
  groups,
  pdmPool,
  onSearch,
  onAdd,
  onRemove,
  canEdit,
  mutatingRegion,
  loadError = null,
  loading = false,
  onRetryLoad,
}) => {
  const styles = usePdmAssignmentStyles();

  const leadsByRegion = React.useMemo(() => {
    const map = new Map<string, PdmAssignment[]>();
    for (const g of groups) map.set(g.region, g.leads);
    return map;
  }, [groups]);

  return (
    <Dialog open={open} onOpenChange={(_e, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <Button
                appearance="subtle"
                aria-label="Close"
                icon={<DismissRegular />}
                onClick={() => onOpenChange(false)}
              />
            }
          >
            Edit regional leads
          </DialogTitle>
          <DialogContent>
            <div className={styles.regionalPanelBody}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3, paddingBottom: '4px' }}>
                A regional lead applies to every partner in its region.
              </Text>
              {loadError && (
                <MessageBar intent="error" role="alert">
                  <MessageBarBody>
                    Regional leads could not be loaded, so this list may be incomplete. {loadError}
                  </MessageBarBody>
                  {onRetryLoad && (
                    <MessageBarActions>
                      <Button size="small" onClick={onRetryLoad} disabled={loading}>
                        {loading ? <Spinner size="tiny" /> : 'Retry'}
                      </Button>
                    </MessageBarActions>
                  )}
                </MessageBar>
              )}
              {regions.length === 0 ? (
                <div className={styles.pickerEmpty}>No regions available.</div>
              ) : (
                regions.map((region) => {
                  const leads = leadsByRegion.get(region) ?? [];
                  const assignedIds = new Set(leads.map((l) => l.pdmId));
                  return (
                    <div key={region} className={styles.regionalPanelRow}>
                      <span className={styles.regionalPanelRegion}>
                        <span
                          className={styles.regionalPanelDot}
                          style={{
                            backgroundColor:
                              PDM_REGION_COLORS[region as PdmRegion] ??
                              tokens.colorNeutralForeground3,
                          }}
                        />
                        {region}
                      </span>
                      <RoleAssignmentCell
                        role="regional_leads"
                        assignments={leads}
                        pdmPool={pdmPool}
                        rowAssignedIds={assignedIds}
                        onAdd={(entry) => onAdd(region, entry)}
                        onRemove={(lead) => onRemove(region, lead)}
                        onSearch={onSearch}
                        inlineAdd
                        addLabel="+ Add"
                        addAriaLabel={`Add regional lead to ${region}`}
                        disabled={
                          !canEdit ||
                          loadError !== null ||
                          loading ||
                          (mutatingRegion !== null && mutatingRegion !== region)
                        }
                        busy={mutatingRegion === region}
                      />
                    </div>
                  );
                })
              )}
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
